/* eslint camelcase: "off" */
/**
 * Render layer for results from the ID-extraction analyzer cascade
 * (`/api/verification/bench/id`). The DocumentsAuditTab re-extract flow
 * is the sole consumer today; the cascade itself runs the zxing-cpp +
 * MRZ + Cloud Vision OCR stack server-side.
 *
 * `IdExtractionResultCard` always shows unmasked values because the only
 * caller invokes it after an audited download — the admin is comparing
 * the OCR'd value against the stored credential record.
 */
import React from 'react';
import { Tag } from '@jem-open/jem-ui';

import { FraudSeverity, FraudSignals } from '../../../../services/verificationClient';
import { FRAUD_SIGNAL_DETAILS, signalSeverity } from './identityVerifications/signals';

export interface ImageDiagnostics {
  size_bytes: number;
  content_type: string;
  format?: string;
  mode?: string;
  raw_width?: number;
  raw_height?: number;
  exif_orientation?: number | null;
  exif_orientation_name?: string;
  rotation_applied_degrees?: number;
  processed_width?: number;
  processed_height?: number;
  decode_error?: string;
}

export interface IdAttempt {
  method: string;
  ok: boolean;
  reason: string | null;
  // Optional per-attempt detail. For barcode extractors this is the
  // actual format that decoded (PDF417 / Code128 / Code39 / ITF) since
  // the cascade method name `barcode_code128` is a catch-all for any
  // 1D barcode.
  detail?: string | null;
}

// Extra fields surfaced by structured extractors. Two sources:
//   * MRZ parser (passport / TD1 ID) — full set including names, nationality
//   * SA ID number decoder — birth_date / sex / citizenship derived from the
//     13-digit structure itself, regardless of how the number was extracted
export interface DocumentExtraFields {
  // 'mrz_format' is set when source is MRZ (td3/td2/td1).
  mrz_format?: string;
  // 'format' is set when source is the SA-ID number decoder (value: 'sa_id').
  format?: string;
  document_number?: string | null;
  surname?: string | null;
  name?: string | null;
  nationality?: string | null;
  country?: string | null;
  birth_date?: string | null;  // YYMMDD
  expiry_date?: string | null; // YYMMDD
  sex?: string | null;
  // SA-ID-only: 'sa_citizen' | 'permanent_resident' | 'unknown'
  citizenship?: string | null;
  optional_data?: string | null;
}

export interface IdCascadeResult {
  document_kind: 'sa_id' | 'passport';
  ok: boolean;
  id_number: string | null;
  method: string | null;
  reason: string | null;
  attempts: IdAttempt[];
  extra_fields?: DocumentExtraFields;
  fraud_signals?: FraudSignals;
  fraud_severity?: FraudSeverity;
}

export interface IdResponse {
  ok: boolean;
  latency_ms: number;
  results: IdCascadeResult[];
  image?: ImageDiagnostics;
}

// Both ICAO 9303 MRZ dates and SA-ID DOBs are 6-digit YYMMDD strings with
// no century — there's no source of truth for whether `80` is 1980 or
// 2080, so we render just what's on the document and let the human
// interpret. The previous version inferred a century with simple
// arithmetic, which produced confidently-wrong-looking guesses.
function formatMrzDate(yymmdd: string): string {
  if (!/^\d{6}$/.test(yymmdd)) return yymmdd;
  return `${yymmdd.slice(0, 2)}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
}

// PII masking for the extracted number display.
//   SA ID    → last 4 visible, e.g. *********9087
//   Passport → first 2 + last 2 visible, e.g. A1*****78
// `showFull=true` returns the trimmed value as-is — used when the access
// is already audited and the admin needs the unmasked value for a
// credential comparison.
export function maskIdNumber(
  value: string,
  kind: 'sa_id' | 'passport',
  showFull = false
): string {
  const trimmed = value.trim();
  if (showFull) return trimmed;
  if (kind === 'sa_id') {
    if (trimmed.length <= 4) return '*'.repeat(trimmed.length);
    return '*'.repeat(trimmed.length - 4) + trimmed.slice(-4);
  }
  if (trimmed.length <= 4) return '*'.repeat(trimmed.length);
  return `${trimmed.slice(0, 2)}${'*'.repeat(trimmed.length - 4)}${trimmed.slice(-2)}`;
}

// PII masking for DOB. YYMMDD → mask first 3 digits, show last 3 (last
// month digit + day), e.g. 800115 → **-*1-15. `showFull` returns the
// formatted YY-MM-DD as-is.
export function maskDob(yymmdd: string, showFull = false): string {
  if (!/^\d{6}$/.test(yymmdd)) return yymmdd;
  if (showFull) return `${yymmdd.slice(0, 2)}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;
  return `**-*${yymmdd.charAt(3)}-${yymmdd.slice(4, 6)}`;
}

function hasAnyDocumentField(fields: DocumentExtraFields): boolean {
  return Boolean(
    fields.surname ||
      fields.name ||
      fields.nationality ||
      fields.birth_date ||
      fields.expiry_date ||
      fields.sex ||
      fields.citizenship ||
      fields.mrz_format ||
      fields.format
  );
}

// Human label for the source of the decoded fields:
//   MRZ parser   → "from MRZ" (TD3/TD2/TD1 set on mrz_format)
//   SA-ID parser → "from SA ID number" (format === 'sa_id')
function decodedSourceLabel(fields: DocumentExtraFields): string {
  if (fields.mrz_format) return 'from MRZ';
  if (fields.format === 'sa_id') return 'from SA ID number';
  return 'from document';
}

// Map fraud severity to a jem-ui Tag variant. Mirrors the backend
// SEVERITY_ORDER (ok < low < medium < high < critical, plus unknown).
function severityVariant(
  severity: FraudSeverity | undefined
): 'success' | 'pending' | 'processing' | 'failed' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'failed';
    case 'medium':
      return 'pending';
    case 'low':
      return 'processing';
    case 'ok':
      return 'success';
    case 'unknown':
    default:
      return 'processing';
  }
}

function kindLabel(kind: 'sa_id' | 'passport'): string {
  return kind === 'sa_id' ? 'SA ID cascade' : 'Passport cascade';
}

export const IdExtractionResultCard: React.FC<{
  result: IdCascadeResult;
  showFull?: boolean;
}> = ({ result: r, showFull }) => {
  const fraudSignals = r.fraud_signals;
  return (
    <div className="mb-sm rounded-lg border border-greyscale-border-subtle bg-white p-sm">
      <div className="mb-xxs flex items-center justify-between">
        <div className="text-xs font-semibold">{kindLabel(r.document_kind)}</div>
        <Tag variant={r.ok ? 'success' : 'failed'}>
          {r.ok ? `extracted (${r.method})` : 'not extracted'}
        </Tag>
      </div>

      <div className="grid grid-cols-2 gap-xxs text-xs">
        <div className="text-greyscale-text-caption">Extracted number</div>
        <div className="text-right font-mono font-medium">
          {r.id_number ? maskIdNumber(r.id_number, r.document_kind, showFull) : '—'}
        </div>

        <div className="text-greyscale-text-caption">Method</div>
        <div className="text-right font-medium">{r.method || '—'}</div>

        {r.reason && (
          <>
            <div className="text-greyscale-text-caption">Failure reason</div>
            <div className="text-right font-medium text-error-text-label">{r.reason}</div>
          </>
        )}
      </div>

      <div className="mt-xs">
        <div className="mb-xxs text-xxs text-greyscale-text-caption">Cascade attempts</div>
        <div className="flex flex-col gap-xxs">
          {r.attempts.map((a) => (
            <div key={a.method} className="flex items-center gap-xxs text-xs">
              <Tag variant={a.ok ? 'success' : 'failed'}>{a.method}</Tag>
              <span className="text-greyscale-text-caption">
                {a.ok
                  ? a.detail
                    ? `decoded (${a.detail})`
                    : 'decoded'
                  : a.reason || 'failed'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Fraud signals — categorical flags computed from the document.
          Severity colour matches the backend SIGNAL_SEVERITY mapping. */}
      {fraudSignals && (
        <div className="mt-xs">
          <div className="mb-xxs flex items-center justify-between">
            <div className="text-xxs text-greyscale-text-caption">Fraud signals</div>
            {r.fraud_severity && (
              <Tag variant={severityVariant(r.fraud_severity)}>overall: {r.fraud_severity}</Tag>
            )}
          </div>
          <div className="flex flex-col gap-xxs">
            {FRAUD_SIGNAL_DETAILS.map(({ key, label }) => {
              const value = fraudSignals[key];
              const sev = signalSeverity(key, value);
              return (
                <div key={key} className="flex items-center justify-between gap-xxs text-xs">
                  <span className="text-greyscale-text-caption">{label}</span>
                  <div className="flex items-center gap-xxs">
                    <span className="font-mono">{String(value ?? 'unknown')}</span>
                    <Tag variant={severityVariant(sev)}>{sev}</Tag>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Decoded fields. Sources:
            * MRZ parser — surfaces names, nationality, DOB, expiry, sex,
              etc. from the passport's machine-readable zone.
            * SA-ID number decoder — surfaces DOB, sex, and citizenship
              status from the structure of the 13-digit ID itself.
              Applies to any SA-ID extraction (barcode + OCR alike). */}
      {r.extra_fields && hasAnyDocumentField(r.extra_fields) && (
        <div className="mt-xs">
          <div className="mb-xxs text-xxs text-greyscale-text-caption">
            Decoded fields ({decodedSourceLabel(r.extra_fields)})
          </div>
          <div className="grid grid-cols-2 gap-xxs text-xs">
            {r.extra_fields.surname && (
              <>
                <div className="text-greyscale-text-caption">Surname</div>
                <div className="text-right font-mono">{r.extra_fields.surname}</div>
              </>
            )}
            {r.extra_fields.name && (
              <>
                <div className="text-greyscale-text-caption">Given names</div>
                <div className="text-right font-mono">{r.extra_fields.name}</div>
              </>
            )}
            {r.extra_fields.nationality && (
              <>
                <div className="text-greyscale-text-caption">Nationality</div>
                <div className="text-right font-mono">{r.extra_fields.nationality}</div>
              </>
            )}
            {r.extra_fields.birth_date && (
              <>
                <div className="text-greyscale-text-caption">Date of birth</div>
                <div className="text-right font-mono">
                  {maskDob(r.extra_fields.birth_date, showFull)}
                </div>
              </>
            )}
            {r.extra_fields.expiry_date && (
              <>
                <div className="text-greyscale-text-caption">Expiry</div>
                <div className="text-right font-mono">
                  {formatMrzDate(r.extra_fields.expiry_date)}
                </div>
              </>
            )}
            {r.extra_fields.sex && (
              <>
                <div className="text-greyscale-text-caption">Sex</div>
                <div className="text-right font-mono">{r.extra_fields.sex}</div>
              </>
            )}
            {r.extra_fields.citizenship && (
              <>
                <div className="text-greyscale-text-caption">Citizenship</div>
                <div className="text-right font-mono">{r.extra_fields.citizenship}</div>
              </>
            )}
            {r.extra_fields.mrz_format && (
              <>
                <div className="text-greyscale-text-caption">MRZ format</div>
                <div className="text-right font-mono">
                  {r.extra_fields.mrz_format.toUpperCase()}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
